import React, { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useEventStore } from "../../../store/eventStore";
import SelectServiceModal from "./SelectServiceModal";
import PrepairAssignModal from "./PrepairAssignModal";
import AssignModal from "./AssignModal";
import {
	CalendarIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
	SparklesIcon,
} from "@heroicons/react/24/outline";
import { BiTrip } from "react-icons/bi";

export default function BusSeat() {
	const { user } = useAuthStore();
	const { getEventsByDate, isLoading, error } = useEventStore();

	const [seatData, setSeatData] = useState([]);
	const [searchDate, setSearchDate] = useState("");
	const [localLoading, setLocalLoading] = useState(false);
	const [selectedIndexes, setSelectedIndexes] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const initialLoadRef = useRef(true);

	const [isSelectServiceOpen, setIsSelectServiceOpen] = useState(false);
	const [isPrepareModalOpen, setIsPrepareModalOpen] = useState(false);
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [selectedService, setSelectedService] = useState(null);
	const [selectedSeatmap, setSelectedSeatmap] = useState(null);
	const [preparedAssignData, setPreparedAssignData] = useState(null);

	useEffect(() => {
		const today = new Date().toISOString().split("T")[0];
		setSearchDate(today);
	}, []);

	useEffect(() => {
		if (initialLoadRef.current && searchDate) {
			handleSearch();
			initialLoadRef.current = false;
		}
	}, [searchDate]);

	const handleClearDate = () => {
		setSearchDate("");
	};

	const handleSearch = async () => {
		if (!searchDate) return;

		setLocalLoading(true);
		try {
			const formattedDate = searchDate.replace(/-/g, "");
			const result = await getEventsByDate(formattedDate, formattedDate);
			if (result.success) {
				const transformedData = (result.data || []).map((item) => ({
					ev_cd: `${item.ev_ym}-${item.ev_seq}`,
					ev_ym: item.ev_ym,
					ev_seq: item.ev_seq,
					ev_nm: item.ev_nm,
					start_day: item.start_day,
					reservation_count: item.reservation_count,
				}));
				setSeatData(transformedData);
			} else {
				setSeatData([]);
			}
		} catch (err) {
			console.error("버스 행사 목록 조회 실패:", err);
			setSeatData([]);
		} finally {
			setLocalLoading(false);
		}
	};

	const handleSelectAll = () => {
		const newSelectAll = !selectAll;
		setSelectAll(newSelectAll);
		if (newSelectAll) {
			setSelectedIndexes(seatData.map((_, index) => index));
		} else {
			setSelectedIndexes([]);
		}
	};

	const handleSelectItem = (index) => {
		const selectedIndex = selectedIndexes.indexOf(index);
		const newSelectedIndexes = [...selectedIndexes];

		if (selectedIndex === -1) {
			newSelectedIndexes.push(index);
		} else {
			newSelectedIndexes.splice(selectedIndex, 1);
		}

		setSelectedIndexes(newSelectedIndexes);
		setSelectAll(newSelectedIndexes.length === seatData.length);
	};

	const handleSeatAssignClick = () => {
		if (selectedIndexes.length === 0) {
			alert("좌석을 배치할 행사를 선택해주세요.");
			return;
		}
		if (selectedIndexes.length > 1) {
			alert("버스 배정은 한 번에 한 개 행사만 처리할 수 있습니다.");
			return;
		}

		const event = seatData[selectedIndexes[0]];
		setSelectedEvent(event);
		setIsSelectServiceOpen(true);
	};

	const handleServiceConfirm = (service, seatmapLayout) => {
		setSelectedService(service);
		setSelectedSeatmap(seatmapLayout || null);
		setIsSelectServiceOpen(false);
		setIsPrepareModalOpen(true);
	};

	const handlePrepare = ({ eventData, busService, seatmapLayout, assignData }) => {
		setPreparedAssignData({
			eventData,
			busService,
			seatmapLayout,
			assignData,
		});
		setIsPrepareModalOpen(false);
		setIsAssignModalOpen(true);
	};

	const formatDate = (dateStr) => {
		if (!dateStr || dateStr.length !== 8) return dateStr || "-";
		return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
	};

	const SearchBox = (
		<div className="bg-white shadow rounded-lg p-6 mb-6">
			<div className="flex flex-nowrap items-center justify-between gap-4">
				<div className="flex flex-nowrap items-center gap-4">
					<div className="relative flex items-center">
						<label htmlFor="searchDate" className="mr-2 text-sm font-medium text-gray-700">
							출발일자
						</label>
						<div className="relative">
							<input
								type="date"
								id="searchDate"
								value={searchDate}
								onChange={(e) => setSearchDate(e.target.value)}
								className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
							/>
							<CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							{searchDate && (
								<button onClick={handleClearDate} className="absolute right-2 top-1/2 -translate-y-1/2">
									<XMarkIcon className="h-4 w-4 text-gray-400" />
								</button>
							)}
						</div>
					</div>

					<button
						onClick={handleSearch}
						disabled={localLoading}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition disabled:opacity-50"
					>
						<div className="flex items-center">
							{localLoading ? (
								<>
									<svg
										className="animate-spin h-4 w-4 mr-1"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									검색 중...
								</>
							) : (
								<>
									<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
									검색하기
								</>
							)}
						</div>
					</button>
				</div>

				<button
					onClick={handleSeatAssignClick}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
				>
					<SparklesIcon className="h-6 w-5 mr-2" />
					좌석배치실행
				</button>
			</div>
		</div>
	);

	const DataBox = (
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th className="pl-6 pr-3 pt-2 pb-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							<input
								type="checkbox"
								checked={selectAll}
								onChange={handleSelectAll}
								className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
							/>
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">번호</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">행사명</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">출발일</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">예약건수</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{seatData && seatData.length > 0 ? (
						seatData.map((item, index) => (
							<tr key={index} className={selectedIndexes.includes(index) ? "bg-blue-50" : ""}>
								<td className="pl-6 pr-3 py-3 whitespace-nowrap">
									<input
										type="checkbox"
										checked={selectedIndexes.includes(index)}
										onChange={() => handleSelectItem(index)}
										className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
									/>
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{item.ev_nm || "-"}</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(item.start_day)}</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
										{item.reservation_count || 0}건
									</span>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="5" className="px-3 py-3 text-center text-sm text-gray-500">
								{error ? (
									<div className="text-red-500">
										<p>오류가 발생했습니다.</p>
										<p>{error}</p>
									</div>
								) : (
									<div className="flex flex-col items-center">
										<BiTrip className="h-10 w-10 text-gray-400 mb-2" />
										<p>조회된 행사 정보가 없습니다.</p>
										<p className="text-sm">날짜를 선택하고 검색해 주세요.</p>
									</div>
								)}
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">버스 좌석배치</h1>
			<p className="text-gray-600 mb-4">버스 좌석을 배정하는 페이지입니다.</p>

			{SearchBox}

			<div className="bg-white shadow rounded-lg overflow-hidden">
				{localLoading ? (
					<div className="flex justify-center items-center p-12">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : (
					DataBox
				)}
			</div>

			<SelectServiceModal
				isOpen={isSelectServiceOpen}
				onClose={() => setIsSelectServiceOpen(false)}
				eventData={selectedEvent}
				onConfirm={handleServiceConfirm}
			/>
			<PrepairAssignModal
				isOpen={isPrepareModalOpen}
				onClose={() => setIsPrepareModalOpen(false)}
				eventData={selectedEvent}
				busService={selectedService}
				seatmapLayout={selectedSeatmap}
				onPrepare={handlePrepare}
			/>
			<AssignModal
				isOpen={isAssignModalOpen}
				onClose={() => {
					setIsAssignModalOpen(false);
					setSelectedService(null);
					setSelectedSeatmap(null);
					setPreparedAssignData(null);
				}}
				assignmentData={preparedAssignData}
			/>
		</div>
	);
}
